import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import * as React from "react";
import { useState } from "react";
import { submitContactEnquiry } from "@/utils/contact.functions";
import { AlertCircle, BookOpen, Calendar } from "lucide-react";
import { Section } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import contactPhoto from "@/assets/contact-call.jpg";
import boardroomAssetSrc from "@/assets/boardroom.png";
import { SITE_URL } from "@/lib/seo";
const boardroomAsset = { url: boardroomAssetSrc };

export const Route = createFileRoute("/contact")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/contact/` }],
    meta: [
      { title: "Contact | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Tell us what's going on with your managers. A real person reads every message and replies within one business day.",
      },
      { property: "og:title", content: "Contact Leadership Edge Live" },
      {
        property: "og:description",
        content:
          "One reply from one person, within one business day. If we're not the right fit, we'll say so.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Contact,
});

const fields = [
  { name: "name", label: "Full name", type: "text" },
  { name: "email", label: "Email", type: "email" },
] as const;

function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const sendEnquiry = useServerFn(submitContactEnquiry);
  // When the form first appeared — used to reject instant bot submissions.
  const openedAt = React.useRef(Date.now());
  // Set when the visitor came here from their assessment results.
  const [hasAssessmentPdf, setHasAssessmentPdf] = useState(false);

  React.useEffect(() => {
    import("@/lib/assessment-handoff")
      .then(({ takeAssessmentPdf }) => setHasAssessmentPdf(Boolean(takeAssessmentPdf())))
      .catch(() => setHasAssessmentPdf(false));
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSending(true);
    setError("");
    try {
      const { takeAssessmentPdf, clearAssessmentHandoff } =
        await import("@/lib/assessment-handoff");
      const assessmentPdfBase64 = takeAssessmentPdf();
      await sendEnquiry({
        data: {
          name: String(form.get("name") ?? ""),
          email: String(form.get("email") ?? ""),
          notes: String(form.get("notes") ?? ""),
          website: String(form.get("website") ?? ""),
          elapsedMs: Date.now() - openedAt.current,
          ...(assessmentPdfBase64 ? { assessmentPdfBase64 } : {}),
        },
      });
      clearAssessmentHandoff();
      setSubmitted(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      // Suspected spam gets the same confirmation, so bots learn nothing.
      if (message === "spam_suspected") setSubmitted(true);
      else setError(message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <section className="relative isolate overflow-hidden">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <img
            src={contactPhoto}
            alt=""
            width={1920}
            height={1088}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)",
            }}
          />
        </div>
        <div className="relative mx-auto flex min-h-[620px] max-w-6xl items-center px-6 py-16 md:py-20">
          <div className="max-w-[600px]">
            <h1
              className="font-sans text-4xl font-bold leading-[1.05] tracking-tight text-[#111111] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Tell us what's going on.{" "}
              <span className="text-[#693D94]">We'll tell you if we can help.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              A real person reads every message and replies within one business day. If we're not
              the right fit, we'll say so.
            </p>
          </div>
        </div>
      </section>

      <Section tone="default">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr]">
          <div>
            <h2
              className="text-3xl font-semibold leading-tight text-ink md:text-4xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              What a conversation covers
            </h2>
            <ul className="mt-6 space-y-6">
              {[
                { icon: AlertCircle, text: "What's breaking with your managers right now" },
                { icon: BookOpen, text: "Which courses match, and which don't" },
                { icon: Calendar, text: "Timing, group size, and what it costs" },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-4">
                  <Icon
                    className="h-6 w-6 flex-shrink-0 text-ink"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                  <span className="text-base leading-relaxed text-muted-foreground">{text}</span>
                </li>
              ))}
            </ul>

            <p className="mt-5 text-sm text-muted-foreground">{"\n"}</p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-2xl border border-border/60 bg-lavender p-8 shadow-brand md:p-9"
          >
            {submitted ? (
              <div className="py-6 text-center">
                <h3
                  className="text-2xl font-semibold leading-tight text-ink"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Got it.
                </h3>
                <p className="mt-3 text-sm text-muted-foreground">
                  We'll be in touch within one business day.
                </p>
              </div>
            ) : (
              <>
                <h3
                  className="text-2xl font-semibold leading-tight text-ink"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Send us a note
                </h3>
                {hasAssessmentPdf ? (
                  <p className="mt-3 text-sm text-muted-foreground">
                    Your Leadership Gaps Assessment results will be attached to this message.
                  </p>
                ) : null}

                <div className="mt-6 grid gap-5">
                  {fields.map((f) => (
                    <div key={f.name} className="grid gap-1.5">
                      <Label
                        htmlFor={f.name}
                        className="text-xs font-semibold uppercase tracking-wider text-primary"
                      >
                        {f.label}
                      </Label>
                      <Input
                        required
                        id={f.name}
                        name={f.name}
                        type={f.type}
                        className="bg-background"
                      />
                    </div>
                  ))}
                  <div className="grid gap-1.5">
                    <Label
                      htmlFor="notes"
                      className="text-xs font-semibold uppercase tracking-wider text-primary"
                    >
                      What is prompting your outreach?
                    </Label>
                    <Textarea
                      required
                      id="notes"
                      name="notes"
                      rows={4}
                      className="rounded-[4px] border-border bg-background focus-visible:ring-2 focus-visible:ring-ring/40"
                    />
                  </div>
                  {/* Honeypot: hidden from people, irresistible to bots. */}
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      id="website"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                </div>
                {error ? <p className="mt-5 text-sm text-destructive">{error}</p> : null}
                <button
                  type="submit"
                  disabled={sending}
                  className="mt-7 inline-flex w-full items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {sending ? "Sending…" : "Send message"}
                </button>
              </>
            )}
          </form>
        </div>
      </Section>

      <AdvisorCTA
        title="Not ready to send a note?"
        body="Take the Leadership Gaps Assessment, or look through the course catalog first. Both take a few minutes and cost nothing."
        primaryLabel="Take the Leadership Gaps Assessment"
        primaryTo="/leadership-gaps-assessment"
        secondaryLabel="View the Course Catalog"
        secondaryTo="/course-catalog"
        backgroundImage={boardroomAsset.url}
      />
    </>
  );
}
